import { useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { LoadingSection } from '../../common/LoadingSection';
import { Button } from '@mui/material';
import ReplyList from '../../common/comments/ReplyList';
import UserDetail from '../../common/comments/UserDetail';
import { goApi } from '../../services';

const timeAgo = (date: string) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);

    const intervals: any = {
        year: 31536000,
        month: 2592000,
        day: 86400,
        hour: 3600,
        minute: 60,
    };

    for (const key in intervals) {
        const value = Math.floor(seconds / intervals[key]);
        if (value >= 1) {
            return `about ${value} ${key}${value > 1 ? 's' : ''} ago`;
        }
    }
    return 'just now';
};

const CommentHeader = ({ user, createdAt }: any) => {
    return (
        <div className="flex justify-between items-center">
            {/* Left: Avatar + Time */}
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-semibold">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="flex flex-col">
                    <p>{user?.name}</p>
                    <div className="text-sm text-gray-500">{timeAgo(createdAt)}</div>
                </div>
            </div>
        </div>
    );
};

const CommentsList = () => {
    const params = useParams();
    const dataProvider = useDataProvider();
    const [newComment, setNewComment] = useState<string>('');
    const [replyContent, setReplyContent] = useState<{ [key: string]: string }>({});
    const notify = useNotify();
    const queryClient = useQueryClient();
    const [commentsList, setCommentsList] = useState<any[]>([]);
    const [replyList, setReplyList] = useState([]) as any;
    const [addReplyCalled, setAddReplyCalled] = useState(false);
    const [activeComment, setActiveComment] = useState<number | null>(null);
    const [replyingTo, setReplyingTo] = useState<number | null>(null);
    const parentType = params.parentType ?? 'comments';

    const toggleReplies = (id: number) => {
        setActiveComment(prev => (prev === id ? null : id));
    };
    const [loading, setLoading] = useState(false);

    const fetchComments = async () => {
        if (!params?.parentId || !params?.parentType) return;

        setLoading(true);
        try {
            const res = await goApi.get(
                `/comments?parentId=${params.parentId}&parentType=${params.parentType}`,
            );

            setCommentsList(res?.data?.items || []);
        } catch (err) {
            notify('Error fetching comments', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchComments();
    }, [params?.parentId, params?.parentType]);

    // Delete comment mutation
    const handleAddComment = async () => {
        if (!newComment.trim()) {
            notify('Comment cannot be empty', { type: 'warning' });
            return;
        }

        if (!params?.parentId || !params?.parentType) {
            notify('Invalid parent information', { type: 'error' });
            return;
        }

        try {
            await goApi.post('/comments', {
                parentId: params.parentId,
                parentType: params.parentType,
                content: newComment,
            });

            notify('Comment added', { type: 'success' });
            setNewComment('');
            fetchComments();
        } catch (err) {
            notify('Error adding comment', { type: 'error' });
        }
    };

    const handleDeleteComment = async (id: string) => {
        try {
            await goApi.delete(`/comments/${id}`);
            notify('Comment deleted', { type: 'success' });
            fetchComments();
        } catch (err) {
            notify('Error deleting comment', { type: 'error' });
        }
    };

    const handleAddReply = async (parentId: number) => {
        const content = replyContent[parentId]?.trim();

        if (!content) {
            notify('Reply cannot be empty', { type: 'warning' });
            return;
        }

        if (content.length < 3) {
            notify('Reply must be at least 3 characters long', { type: 'warning' });
            return;
        }

        try {
            await goApi.post('/comments', {
                parentId: parentId,
                parentType: 'COMMENT',
                content: content,
            });

            notify('Reply added', { type: 'success' });
            setReplyContent(prev => ({ ...prev, [parentId]: '' }));
            setReplyingTo(null);
            fetchComments();
        } catch (err) {
            notify('Error adding reply', { type: 'error' });
        }
    };

    return (
        <>
            <div className="bg-gray-100 p-10">
                <h2 className="text-4xl font-semibold text-center pb-5">Comments</h2>
                <>
                    {loading ? (
                        <LoadingSection />
                    ) : (
                        <>
                            <div className=" flex gap-5 my-3 mx-auto">
                                <input
                                    type="text"
                                    className="border border-gray-300 rounded-lg  w-4/5 p-2"
                                    placeholder="Add a new comment..."
                                    value={newComment}
                                    onChange={e => setNewComment(e.target.value)}
                                />

                                <Button
                                    className="bg-blue-500 text-white px-4 py-2 text-md rounded-lg hover:bg-blue-600"
                                    onClick={handleAddComment}
                                >
                                    Add Comment
                                </Button>
                            </div>

                            <div className="space-y-4">
                                {commentsList?.map((comment: any) => (
                                    <div
                                        key={comment?.id}
                                        className="bg-white p-4 shadow rounded-md border border-gray-200"
                                    >
                                        <div className="flex justify-between items-center cursor-pointer">
                                            <div className="flex gap-3  w-4/6">
                                                <div className="flex gap-3 flex-col w-full ">
                                                    <h3 className=" text-gray-800 w-2/3">
                                                        {/* <UserDetail userId={comment?.user_id} createdAt={comment?.created_at} /> */}
                                                        <CommentHeader
                                                            user={comment.user}
                                                            createdAt={comment.createdAt}
                                                        />
                                                    </h3>

                                                    <div className="flex flex-col pl-16">
                                                        <p className="text-gray-900">
                                                            {comment?.content}
                                                        </p>
                                                        {replyingTo === comment.id ? (
                                                            <div className="mt-4">
                                                                <input
                                                                    type="text"
                                                                    className="border border-gray-300 rounded-lg w-full p-2"
                                                                    placeholder="Write a reply..."
                                                                    value={
                                                                        replyContent[comment.id] ||
                                                                        ''
                                                                    }
                                                                    onChange={e =>
                                                                        setReplyContent(prev => ({
                                                                            ...prev,
                                                                            [comment.id]:
                                                                                e.target.value,
                                                                        }))
                                                                    }
                                                                />

                                                                <div className="flex gap-10 justify-end mt-3">
                                                                    <Button
                                                                        onClick={() =>
                                                                            setReplyingTo(null)
                                                                        }
                                                                        className="text-blue-500 text-sm hover:underline w-[50px]"
                                                                    >
                                                                        Cancel
                                                                    </Button>

                                                                    <Button
                                                                        className="bg-green-500 text-white px-4 py-2 mt-2 rounded-lg hover:bg-green-600"
                                                                        onClick={() =>
                                                                            handleAddReply(
                                                                                comment.id,
                                                                            )
                                                                        }
                                                                        variant="contained"
                                                                    >
                                                                        Reply
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <Button
                                                                onClick={() =>
                                                                    setReplyingTo(prev =>
                                                                        prev === comment.id
                                                                            ? null
                                                                            : comment.id,
                                                                    )
                                                                }
                                                                className="text-blue-500 text-sm hover:underline w-[50px]"
                                                            >
                                                                {replyingTo === comment.id
                                                                    ? 'Cancel'
                                                                    : 'Reply'}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex gap-4 items-center">
                                                <Button
                                                    onClick={() => toggleReplies(comment?.id)}
                                                    className="text-gray-500  text-sm hover:font-bold"
                                                >
                                                    {activeComment === comment?.id
                                                        ? 'Hide Replies'
                                                        : 'See Replies'}
                                                </Button>

                                                <Button
                                                    color="error"
                                                    className="text-red-500 text-sm  hover:font-bold"
                                                    onClick={() => handleDeleteComment(comment.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        </div>

                                        {activeComment === comment?.id && (
                                            <>
                                                <ReplyList
                                                    parentId={comment?.id}
                                                    parentType={'COMMENT'}
                                                    replyList={replyList}
                                                    setReplyList={setReplyList}
                                                    addReplyCalled={addReplyCalled}
                                                />
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            </div>
        </>
    );
};

export default CommentsList;
